import { FC } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Slider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';

export interface ReviewRow {
    id: string;
    wordText: string;
    meaningText: string;
    difficultyBase: number;
    dictId: number | null;
    pos: string | null;
    phonetic: string | null;
    selected: boolean;
    duplicateOfExisting: boolean;
}

interface Props {
    rows: ReviewRow[];
    onChange: (rows: ReviewRow[]) => void;
    onRestart: () => void;
    onCancel: () => void;
    onSave: () => void;
    saving: boolean;
}

export const StepReview: FC<Props> = ({ rows, onChange, onRestart, onCancel, onSave, saving }) => {
    const selectedCount = rows.filter((r) => r.selected).length;
    const duplicateCount = rows.filter((r) => r.duplicateOfExisting).length;

    const update = (id: string, patch: Partial<ReviewRow>) => {
        onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    };

    const remove = (id: string) => {
        onChange(rows.filter((r) => r.id !== id));
    };

    const allSelected = rows.length > 0 && rows.every((r) => r.selected);
    const someSelected = rows.some((r) => r.selected) && !allSelected;

    return (
        <Stack spacing={2} sx={{ pt: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    생성됨 {rows.length} · 선택됨 {selectedCount} · 중복 {duplicateCount}
                </Typography>
            </Box>
            <Box sx={{ maxHeight: 480, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    ref={(el) => {
                                        if (el) el.indeterminate = someSelected;
                                    }}
                                    onChange={(e) =>
                                        onChange(rows.map((r) => ({ ...r, selected: e.target.checked })))
                                    }
                                />
                            </TableCell>
                            <TableCell>단어</TableCell>
                            <TableCell>뜻</TableCell>
                            <TableCell sx={{ width: 200 }}>난이도</TableCell>
                            <TableCell padding="checkbox" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow key={r.id} sx={{ opacity: r.duplicateOfExisting ? 0.55 : 1 }}>
                                <TableCell padding="checkbox">
                                    <input
                                        type="checkbox"
                                        checked={r.selected}
                                        onChange={(e) => update(r.id, { selected: e.target.checked })}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Stack spacing={0.5}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <TextField
                                                size="small"
                                                value={r.wordText}
                                                onChange={(e) => update(r.id, { wordText: e.target.value })}
                                                fullWidth
                                            />
                                            {r.duplicateOfExisting && (
                                                <Chip label="이미 존재" size="small" color="warning" />
                                            )}
                                        </Stack>
                                        {(r.pos || r.phonetic) && (
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                {r.phonetic && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ fontFamily: 'monospace' }}
                                                    >
                                                        [{r.phonetic}]
                                                    </Typography>
                                                )}
                                                {r.pos && (
                                                    <Chip
                                                        label={r.pos}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                                    />
                                                )}
                                            </Stack>
                                        )}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        value={r.meaningText}
                                        onChange={(e) => update(r.id, { meaningText: e.target.value })}
                                        fullWidth
                                    />
                                </TableCell>
                                <TableCell>
                                    <Slider
                                        value={r.difficultyBase}
                                        onChange={(_, v) => update(r.id, { difficultyBase: v as number })}
                                        min={0}
                                        max={100}
                                        valueLabelDisplay="auto"
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell padding="checkbox">
                                    <IconButton size="small" onClick={() => remove(r.id)} title="삭제">
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            <Box display="flex" justifyContent="space-between" pt={1}>
                <Button onClick={onRestart} disabled={saving}>
                    다시 시작
                </Button>
                <Box display="flex" gap={1}>
                    <Button onClick={onCancel} disabled={saving}>
                        취소
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving || selectedCount === 0}
                        onClick={onSave}
                    >
                        {saving ? '저장 중…' : `선택 저장 (${selectedCount})`}
                    </Button>
                </Box>
            </Box>
        </Stack>
    );
};
